import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { notify } from '../../../notifications';
import type { MathColumn, MathRow } from './mathReportApi';
import {
  CELL_INPUT_PATTERN,
  columnKind,
  formatMoney,
  isAuditClean,
  parseCellInput,
  toEditText,
  toNumber
} from './mathReportCalc';
import type { ColumnKind } from './mathReportCalc';

const BORDER = '1px solid #e2e8f0';

/** Стили заданы один раз на контейнере вложенными селекторами, а не на каждой ячейке */
const gridSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  '& table': { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },

  '& th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    boxSizing: 'border-box',
    padding: '9px 12px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontFamily: 'inherit',
    fontSize: '0.8rem',
    fontWeight: 700,
    textAlign: 'left',
    borderBottom: BORDER,
    whiteSpace: 'nowrap'
  },
  '& th.num': { textAlign: 'right' },
  '& th.center, & td.center': { textAlign: 'center' },

  // Первая колонка (аптека) и колонка скачивания закреплены по краям; угловые ячейки шапки поверх всех
  '& th.first, & td.first': { position: 'sticky', left: 0 },
  '& th.last, & td.last': { position: 'sticky', right: 0 },
  '& th.first, & th.last': { zIndex: 4 },
  '& td.first': { zIndex: 1, fontWeight: 600, borderRight: BORDER },
  '& td.last': { zIndex: 1, padding: '3px 8px', borderLeft: BORDER },

  '& tbody tr': { backgroundColor: '#ffffff' },
  '& tbody tr:hover': { backgroundColor: '#f1f5f9' },
  '& tbody tr.alert': { backgroundColor: '#fef2f2' },
  '& tbody tr.alert:hover': { backgroundColor: '#fee2e2' },

  '& td': {
    boxSizing: 'border-box',
    padding: '6px 12px',
    borderBottom: '1px solid #f1f5f9',
    fontFamily: 'inherit',
    fontSize: '0.82rem',
    color: '#1e293b',
    whiteSpace: 'nowrap'
  },
  '& td.num': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  // Закрепленные ячейки непрозрачны, но наследуют цвет строки (подсветка, наведение)
  '& td.first, & td.last': { backgroundColor: 'inherit' },
  '& td.edit': { cursor: 'pointer' },
  '& td.edit:hover': { backgroundColor: '#e2e8f0' },

  '& svg.ok': { fontSize: 20, color: '#10b981', verticalAlign: 'middle' },
  '& svg.fail': { fontSize: 20, color: '#ef4444', verticalAlign: 'middle' },

  '& input.cell-input': {
    boxSizing: 'border-box',
    width: 110,
    padding: '2px 4px',
    border: 'none',
    borderBottom: '2px solid #3b82f6',
    outline: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    textAlign: 'right'
  },
  '& input.cell-input:disabled': { opacity: 0.6 },

  '& button.download': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 36,
    height: 28,
    padding: 0,
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#475569',
    cursor: 'pointer'
  },
  '& button.download:hover:not(:disabled)': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' },
  '& button.download:disabled': { cursor: 'default', opacity: 0.7 }
};

interface GridColumn {
  field: string;
  headerName: string;
  kind: ColumnKind;
  /** Ячейку можно править (или переключить статус) с учетом прав роли */
  interactive: boolean;
  className: string;
}

function buildGridColumns(columns: MathColumn[], canEditCells: boolean, canCheckStatus: boolean): GridColumn[] {
  return columns.map((column, index) => {
    const kind = columnKind(column.field);
    const interactive = column.editable && (kind === 'status' ? canCheckStatus : canEditCells);
    const classes: string[] = [];
    if (index === 0) classes.push('first');
    if (kind === 'number') classes.push('num');
    if (kind === 'status') classes.push('center');
    if (interactive) classes.push('edit');
    return { field: column.field, headerName: column.headerName, kind, interactive, className: classes.join(' ') };
  });
}

interface CellEdit {
  field: string;
  text: string;
  initial: number | null;
  saving: boolean;
}

function renderValue(kind: ColumnKind, value: unknown): React.ReactNode {
  if (kind === 'status') {
    return toNumber(value) === 1 ? <CheckCircleIcon className="ok" /> : <CancelIcon className="fail" />;
  }
  if (kind === 'audit') return toNumber(value) === 1 ? 'Да' : 'Нет';
  if (kind === 'number') return formatMoney(value);
  return value === null || value === undefined ? '' : String(value);
}

interface RowViewProps {
  row: MathRow;
  columns: GridColumn[];
  canDownload: boolean;
  downloading: boolean;
  onToggleStatus: (id: number, current: number) => void;
  onSaveCell: (id: number, field: string, value: number) => Promise<boolean>;
  onDownload: (row: MathRow) => void;
}

/** Правка ячейки хранится в самой строке: набор текста перерисовывает одну строку, а не всю страницу */
const RowView = memo(({ row, columns, canDownload, downloading, onToggleStatus, onSaveCell, onDownload }: RowViewProps) => {
  const [edit, setEdit] = useState<CellEdit | null>(null);
  const editRef = useRef<CellEdit | null>(null);

  const setEditing = (next: CellEdit | null) => {
    editRef.current = next;
    setEdit(next);
  };

  const startEdit = (column: GridColumn) => {
    // Пока предыдущая правка еще сохраняется, можно переходить к соседней ячейке
    if (editRef.current && !editRef.current.saving) return;

    if (column.kind === 'status') {
      onToggleStatus(row.id, toNumber(row[column.field]) === 1 ? 1 : 0);
      return;
    }
    const initial = toNumber(row[column.field]);
    setEditing({ field: column.field, text: toEditText(initial), initial, saving: false });
  };

  const commit = async () => {
    const current = editRef.current;
    if (!current || current.saving) return;

    if (current.text.trim() === '') {
      setEditing(null);
      return;
    }
    const value = parseCellInput(current.text);
    if (value === null) {
      notify('Введите корректное число', 'error');
      setEditing(null);
      return;
    }
    if (value === current.initial) {
      setEditing(null);
      return;
    }

    const saving: CellEdit = { ...current, saving: true };
    setEditing(saving);
    await onSaveCell(row.id, current.field, value);
    // Если за время сохранения пользователь начал править другую ячейку, ее не трогаем
    if (editRef.current === saving) setEditing(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const current = editRef.current;
    if (!current || !CELL_INPUT_PATTERN.test(event.target.value)) return;
    setEditing({ ...current, text: event.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur();
    if (event.key === 'Escape') setEditing(null);
  };

  return (
    <tr className={isAuditClean(row) ? 'alert' : undefined}>
      {columns.map((column) => (
        <td
          key={column.field}
          className={column.className || undefined}
          onClick={column.interactive ? () => startEdit(column) : undefined}
        >
          {edit && edit.field === column.field ? (
            <input
              className="cell-input"
              autoFocus
              inputMode="decimal"
              value={edit.text}
              disabled={edit.saving}
              onFocus={(event) => event.target.select()}
              onChange={handleChange}
              onBlur={commit}
              onKeyDown={handleKeyDown}
            />
          ) : (
            renderValue(column.kind, row[column.field])
          )}
        </td>
      ))}
      {canDownload && (
        <td className="last center">
          <button
            type="button"
            className="download"
            title="Скачать мат. отчет по аптеке"
            disabled={downloading}
            onClick={() => onDownload(row)}
          >
            {downloading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
          </button>
        </td>
      )}
    </tr>
  );
});

interface MathReportGridProps {
  rows: MathRow[];
  columns: MathColumn[];
  canEditCells: boolean;
  canCheckStatus: boolean;
  canDownload: boolean;
  downloadingId: number | null;
  /** При смене значения таблица прокручивается к началу (другая страница, размер страницы, месяц) */
  scrollKey: string;
  onToggleStatus: (id: number, current: number) => void;
  onSaveCell: (id: number, field: string, value: number) => Promise<boolean>;
  onDownload: (row: MathRow) => void;
}

const MathReportGrid = memo(
  ({
    rows,
    columns,
    canEditCells,
    canCheckStatus,
    canDownload,
    downloadingId,
    scrollKey,
    onToggleStatus,
    onSaveCell,
    onDownload
  }: MathReportGridProps) => {
    const gridColumns = useMemo(
      () => buildGridColumns(columns, canEditCells, canCheckStatus),
      [columns, canEditCells, canCheckStatus]
    );
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [scrollKey]);

    return (
      <Box ref={scrollRef} sx={gridSx}>
        <table>
          <thead>
            <tr>
              {gridColumns.map((column) => (
                <th key={column.field} className={column.className.replace('edit', '').trim() || undefined}>
                  {column.headerName}
                </th>
              ))}
              {canDownload && <th className="last center">МатОтчет</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <RowView
                key={row.id}
                row={row}
                columns={gridColumns}
                canDownload={canDownload}
                downloading={downloadingId === row.id}
                onToggleStatus={onToggleStatus}
                onSaveCell={onSaveCell}
                onDownload={onDownload}
              />
            ))}
          </tbody>
        </table>
      </Box>
    );
  }
);

export default MathReportGrid;
